import { Button, createStyles, makeStyles, MenuItem, Select, Theme, Typography } from "@material-ui/core";
import * as React from "react";
import { SocketMessageType } from "../../../../Definitions/Definitions";
import { useGameContext } from "../../../GameContext/GameContext";
import { OriginalGameParameters, OriginalGamePowerupMode, OriginalGameState } from "../../../GameContext/OriginalGameState";
import { HoverHelp } from "../../HoverHelp";
import { useWindowDimensions } from "../../UseWindowDimensions";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "0.5rem"
        },
        textFlex: {
            display: "flex",
            alignItems: "center",
            justifyContent: "left",
            gap: "0.5rem"
        }
    })
);

export const OriginalGameParamsList = () => {
    const classes = useStyles();
    const gameContext = useGameContext();

    const { actualLocalPlayer, gameParameters } = gameContext.gameState as OriginalGameState;

    const updateParams = (newParams: OriginalGameParameters) => {
        gameContext.updateGameState((prevState: OriginalGameState) => {
            return {
                ...prevState,
                gameParameters: newParams
            }
        });

        gameContext.sendMessage(SocketMessageType.GameParametersChanged, JSON.stringify(newParams));
    }

    const getPowerupModeName = (mode: OriginalGamePowerupMode) => {
        switch (mode) {
            default: return "";
            case OriginalGamePowerupMode.Disabled: return "Off";
            case OriginalGamePowerupMode.Default: return "Default";
            case OriginalGamePowerupMode.Chaos: return "Chaos";
        }
    }

    return (
        <div className={classes.root}>
            <div className={classes.textFlex}>
                <HoverHelp text={gameParameters.minutes == 0 ?
                    "No limit ends the game once the first player completes all words"
                    : "The game will end after this time has elapsed"
                } />
                <Typography variant="body1">Time limit:</Typography>
                {(actualLocalPlayer?.isHost) ?
                    <Select
                        value={gameParameters.minutes}
                        onChange={(e) => updateParams({ ...gameParameters, minutes: e.target.value as number })}
                    >
                        <MenuItem value={0}>No Limit</MenuItem>
                        <MenuItem value={1}>1 Min</MenuItem>
                        {
                            [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((n) => {
                                return <MenuItem key={n} value={n}>{n} Mins</MenuItem>
                            })
                        }
                    </Select>
                    :
                    <Typography variant="body1"><b>{gameParameters.minutes == 0 ? "No Limit" : `${gameParameters.minutes} min`}</b></Typography>
                }
            </div>
            <div className={classes.textFlex}>
                <HoverHelp text={"Gain bonus points at the end of the game based on how much time is remaining and how many words you were able to complete"} />
                <Typography variant="body1">Time bonus:</Typography>
                {(actualLocalPlayer?.isHost) ?
                    <Select
                        value={gameParameters.timeBonusEnabled.toString()}
                        onChange={(e) => updateParams({ ...gameParameters, timeBonusEnabled: e.target.value as boolean })}
                    >
                        <MenuItem value={"true"}>On</MenuItem>
                        <MenuItem value={"false"}>Off</MenuItem>
                    </Select>
                    :
                    <Typography variant="body1"><b>{gameParameters.timeBonusEnabled ? "On" : "Off"}</b></Typography>
                }
            </div>
            <div className={classes.textFlex}>
                <HoverHelp text={"The words you will try to guess will be this many letters"} />
                <Typography variant="body1">Letters:</Typography>
                {(actualLocalPlayer?.isHost) ?
                    <Select
                        value={gameParameters.wordLength}
                        onChange={(e) => updateParams({ ...gameParameters, wordLength: e.target.value as number })}
                    >
                        {
                            [4, 5, 6].map((n) => {
                                return <MenuItem key={n} value={n}>{n}</MenuItem>
                            })
                        }
                    </Select>
                    :
                    <Typography variant="body1"><b>{gameParameters.wordLength}</b></Typography>
                }
            </div>
            <div className={classes.textFlex}>
                <HoverHelp text={"The number of words available to complete during the game"} />
                <Typography variant="body1">Word count:</Typography>
                {(actualLocalPlayer?.isHost) ?
                    <Select
                        value={gameParameters.wordCount}
                        onChange={(e) => updateParams({ ...gameParameters, wordCount: e.target.value as number })}
                    >
                        {
                            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((n) => {
                                return <MenuItem key={n} value={n}>{n}</MenuItem>
                            })
                        }
                    </Select>
                    :
                    <Typography variant="body1"><b>{gameParameters.wordCount}</b></Typography>
                }
            </div>
            <div className={classes.textFlex}>
                <HoverHelp text={"The number of guesses allowed per word"} />
                <Typography variant="body1">Guess count:</Typography>
                {(actualLocalPlayer?.isHost) ?
                    <Select
                        value={gameParameters.guessCount}
                        onChange={(e) => updateParams({ ...gameParameters, guessCount: e.target.value as number })}
                    >
                        {
                            [4, 5, 6, 7].map((n) => {
                                return <MenuItem key={n} value={n}>{n}</MenuItem>
                            })
                        }
                    </Select>
                    :
                    <Typography variant="body1"><b>{gameParameters.guessCount}</b></Typography>
                }
            </div>
            <Typography variant="h6" style={{ margin: "0.5rem 0 -0.5rem 0" }}>EXPERIMENTAL SETTINGS</Typography>
            <div className={classes.textFlex}>
                <HoverHelp text={"Give yourself boosts and sabotage your friends using various powerups"} />
                <Typography variant="body1">Powerups:</Typography>
                {(actualLocalPlayer?.isHost) ?
                    <Select
                        value={gameParameters.activePowerupMode}
                        onChange={(e) => updateParams({ ...gameParameters, activePowerupMode: e.target.value as OriginalGamePowerupMode })}
                    >
                        <MenuItem value={OriginalGamePowerupMode.Disabled}>{getPowerupModeName(OriginalGamePowerupMode.Disabled)}</MenuItem>
                        <MenuItem value={OriginalGamePowerupMode.Default}>{getPowerupModeName(OriginalGamePowerupMode.Default)}</MenuItem>
                        <MenuItem value={OriginalGamePowerupMode.Chaos}>{getPowerupModeName(OriginalGamePowerupMode.Chaos)}</MenuItem>
                    </Select>
                    :
                    <Typography variant="body1"><b>{getPowerupModeName(gameParameters.activePowerupMode)}</b></Typography>
                }
            </div>
        </div>
    );
}